/**
 * App.tsx - Main Application Router
 *
 * Routes adapted from original CareerCard Vike file-based routing.
 * Pages are imported from their original locations.
 */
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";

// Layout components
import Layout from "./components/Layout";

// Auth guard component - redirects to login if not authenticated
import { useAuth } from "./context/AuthContext";

// Loading fallback
const LoadingFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

// Helper to handle both export styles (export default vs export { Page })
const resolveExport = (m: { default?: unknown; Page?: unknown }) => {
  // Try named export first, then default
  return { default: (m.Page || m.default) as React.ComponentType };
};

// Lazy load pages for code splitting
// Public pages
const LandingPage = lazy(() => import("./pages/index/+Page").then(resolveExport));
const PrivacyPage = lazy(() => import("./pages/privacy/+Page").then(resolveExport));
const TermsPage = lazy(() => import("./pages/terms/+Page"));  // default export
const PricingPage = lazy(() => import("./pages/pricing/+Page"));  // default export
const GamificationSupportPage = lazy(() => import("./pages/gamification-support/+Page").then(resolveExport));

// Auth pages - all use default export
const SignInPage = lazy(() => import("./pages/auth/sign-in/+Page"));
const SignUpPage = lazy(() => import("./pages/auth/sign-up/+Page"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/forgot-password/+Page"));
const ResetPasswordPage = lazy(() => import("./pages/auth/reset-password/+Page"));

// Protected pages (main app)
const HomePage = lazy(() => import("./pages/home/+Page").then(resolveExport));
const SettingsPage = lazy(() => import("./pages/settings/+Page").then(resolveExport));
const ProfilePage = lazy(() => import("./pages/profile/+Page").then(resolveExport));
const TimelinePage = lazy(() => import("./pages/timeline/+Page").then(resolveExport));
const RetrospectivesPage = lazy(() => import("./pages/retrospectives/+Page").then(resolveExport));
const RetrospectiveDetailPage = lazy(() => import("./pages/retrospectives/RetroDetailPage"));  // React Router compatible version
const FocusPage = lazy(() => import("./pages/focus/+Page").then(resolveExport));
const InternshipFocusPage = lazy(() => import("./pages/focus/internship/@id/+Page").then(resolveExport));
const PerformanceReviewFocusPage = lazy(() => import("./pages/focus/performance_review/@id/+Page"));  // default export
const JobSearchPage = lazy(() => import("./pages/job-search/+Page").then(resolveExport));
const FilesPage = lazy(() => import("./pages/files/+Page").then(resolveExport));
const ResumesPage = lazy(() => import("./pages/resumes/+Page").then(resolveExport));
const CallbackPage = lazy(() => import("./pages/callback/+Page"));  // OAuth callback handler

// Settings sub-pages
const PrivacySettingsPage = lazy(() => import("./pages/settings/privacy/+Page").then(resolveExport));
const NotificationSettingsPage = lazy(() => import("./pages/settings/notifications/+Page").then(resolveExport));

// Admin pages - use index.ts re-exports
const AdminDashboard = lazy(() => import("./pages/admin").then(m => ({ default: m.AdminDashboard })));
const AdminUsers = lazy(() => import("./pages/admin").then(m => ({ default: m.AdminUsers })));
const AdminNotifications = lazy(() => import("./pages/admin").then(m => ({ default: m.AdminNotifications })));

// Auth guard wrapper component
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  return <>{children}</>;
}

// Admin guard wrapper component
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  // Check if user has admin role
  const isAdmin = user?.role === 'admin' || user?.email?.endsWith('@careercard.com');

  if (!isAdmin) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/gamification-support" element={<GamificationSupportPage />} />

          {/* Auth routes */}
          <Route path="/auth/sign-in" element={<SignInPage />} />
          <Route path="/auth/sign-up" element={<SignUpPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

          {/* Callback route for OAuth */}
          <Route path="/callback" element={<CallbackPage />} />

          {/* Legacy route redirects */}
          <Route path="/login" element={<Navigate to="/auth/sign-in" replace />} />
          <Route path="/dashboard" element={<Navigate to="/home" replace />} />

          {/* Protected routes - wrapped with Layout and RequireAuth */}
          <Route element={<Layout />}>
            <Route
              path="/home"
              element={
                <RequireAuth>
                  <HomePage />
                </RequireAuth>
              }
            />
            <Route
              path="/settings"
              element={
                <RequireAuth>
                  <SettingsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/settings/privacy"
              element={
                <RequireAuth>
                  <PrivacySettingsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/settings/notifications"
              element={
                <RequireAuth>
                  <NotificationSettingsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/profile"
              element={
                <RequireAuth>
                  <ProfilePage />
                </RequireAuth>
              }
            />
            <Route
              path="/timeline"
              element={
                <RequireAuth>
                  <TimelinePage />
                </RequireAuth>
              }
            />
            <Route
              path="/retrospectives"
              element={
                <RequireAuth>
                  <RetrospectivesPage />
                </RequireAuth>
              }
            />
            <Route
              path="/retrospectives/:id"
              element={
                <RequireAuth>
                  <RetrospectiveDetailPage />
                </RequireAuth>
              }
            />
            <Route
              path="/focus"
              element={
                <RequireAuth>
                  <FocusPage />
                </RequireAuth>
              }
            />
            <Route
              path="/focus/internship/:id"
              element={
                <RequireAuth>
                  <InternshipFocusPage />
                </RequireAuth>
              }
            />
            <Route
              path="/focus/performance_review/:id"
              element={
                <RequireAuth>
                  <PerformanceReviewFocusPage />
                </RequireAuth>
              }
            />
            <Route
              path="/job-search"
              element={
                <RequireAuth>
                  <JobSearchPage />
                </RequireAuth>
              }
            />
            <Route
              path="/files"
              element={
                <RequireAuth>
                  <FilesPage />
                </RequireAuth>
              }
            />
            <Route
              path="/resumes"
              element={
                <RequireAuth>
                  <ResumesPage />
                </RequireAuth>
              }
            />

            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <AdminDashboard />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/users"
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <AdminUsers />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/notifications"
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <AdminNotifications />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
          </Route>

          {/* 404 catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
